import { KeyboardEvent, Ref, useRef, useState } from "react";
import Autosuggest, {
  ChangeEvent as AutosuggestChangeEvent,
  RenderInputComponentProps,
  RenderSuggestionsContainerParams,
  SuggestionSelectedEventData,
  SuggestionsFetchRequestedParams,
} from "react-autosuggest";
import { useTranslation } from "react-i18next";
import Field from "components/ui/Field";
import Action from "components/formElements/Action/Action";
import { getPreviousSearch } from "services/dictionary";
import SearchIcon from "images/search.svg?react";
import DropdownIcon from "./images/dropdown.svg?react";
import "./search.css";
import "./SearchInputWithSuggestions.css";

const SEARCH_PLACEHOLDER = "Tool.lookUp";
const MAX_SEARCH_LENGTH = 512;

type FocusableInput = {
  focus: () => void;
  blur: () => void;
};

type Props = {
  value: string;
  onValueChanged: (value: string) => void;
  onSearch: (value?: string) => void;
};

export default function SearchInputWithSuggestions(props: Props) {
  const { t } = useTranslation();
  const [suggestions, setSuggestions] = useState<string[]>(getPreviousSearch);
  const [isSuggestionsContainerOpened, setIsSuggestionsContainerOpened] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const searchInputRef = useRef<FocusableInput | null>(null);

  const getSuggestions = (input: string) => {
    const history = getPreviousSearch();
    const inputValue = input.trim().toLowerCase();
    if (inputValue.length === 0) {
      return history;
    }
    return history.filter(
      suggestion => suggestion.toLowerCase().slice(0, inputValue.length) === inputValue
    );
  };

  const onChange = (_: unknown, { newValue }: AutosuggestChangeEvent) => {
    props.onValueChanged(newValue);
    if (!isSuggestionsContainerOpened) {
      setIsSuggestionsContainerOpened(true);
    }
  };

  const onSuggestionsFetchRequested = ({
    value: input,
  }: SuggestionsFetchRequestedParams) => {
    setSuggestions(getSuggestions(input));
  };

  const onSuggestionsClearRequested = () => {
    setSuggestions([]);
  };

  const onArrowClick = () => {
    const shouldBeOpened = !isSuggestionsContainerOpened;
    setIsSuggestionsContainerOpened(shouldBeOpened);
    setSuggestions(getSuggestions(props.value));
    if (shouldBeOpened) {
      searchInputRef.current?.focus();
    } else {
      searchInputRef.current?.blur();
    }
  };

  const closeSuggestions = () => {
    setIsSuggestionsContainerOpened(false);
    setIsFocused(false);
  };

  const onSearchClick = () => {
    closeSuggestions();
    props.onSearch();
  };

  const onKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      // Proceed to search after click on Enter.
      onSearchClick();
    }
  };

  const onSuggestionSelected = (
    _: unknown,
    { suggestionValue }: SuggestionSelectedEventData<string>
  ) => {
    if (suggestionValue) {
      closeSuggestions();
      props.onSearch(suggestionValue);
    }
  };

  // The lookup pill is the kit ui/Field; Autosuggest still owns the input's
  // props and needs a handle on the real control, which Field forwards as its
  // ref. The history-dropdown and search actions ride in the pill's trailing
  // slot.
  const renderInputComponent = (inputProps: RenderInputComponentProps) => {
    const {
      key: inputKey,
      ref: inputRef,
      ...restInputProps
    } = inputProps as RenderInputComponentProps & { key?: string };

    return (
      <Field
        key={inputKey}
        ref={inputRef as Ref<HTMLInputElement>}
        {...restInputProps}
        maxLength={MAX_SEARCH_LENGTH}
        onKeyPress={onKeyPress}
        trailing={
          <span className="input-actions-container">
            <Action
              className="dropdown-btn"
              aria-label={t("Tool.recentSearches")}
              title={t("Tool.recentSearches")}
              onClick={onArrowClick}
            >
              <DropdownIcon aria-hidden />
            </Action>
            <Action
              className="search-btn"
              aria-label={t("Common.Search")}
              title={t("Common.Search")}
              onClick={onSearchClick}
            >
              <SearchIcon aria-hidden />
            </Action>
          </span>
        }
      />
    );
  };

  const renderSuggestionsContainer = ({
    containerProps,
    children,
  }: RenderSuggestionsContainerParams) => {
    if (!isSuggestionsContainerOpened && !isFocused) {
      return null;
    }
    return <div {...containerProps}>{children}</div>;
  };

  const storeInputReference = (autosuggest: Autosuggest<string> | null) => {
    if (autosuggest !== null) {
      searchInputRef.current = (autosuggest.input as unknown as FocusableInput) ?? null;
    }
  };

  return (
    <Autosuggest
      suggestions={suggestions}
      alwaysRenderSuggestions={true}
      highlightFirstSuggestion={true}
      getSuggestionValue={suggestion => suggestion}
      focusInputOnSuggestionClick={false}
      onSuggestionsFetchRequested={onSuggestionsFetchRequested}
      onSuggestionsClearRequested={onSuggestionsClearRequested}
      onSuggestionSelected={onSuggestionSelected}
      renderSuggestion={suggestion => <div className="suggestion">{suggestion}</div>}
      renderInputComponent={renderInputComponent}
      renderSuggestionsContainer={renderSuggestionsContainer}
      inputProps={{
        value: props.value,
        onChange,
        onFocus: () => setIsFocused(true),
        onBlur: () => setIsFocused(false),
        placeholder: t(SEARCH_PLACEHOLDER),
      }}
      ref={storeInputReference}
    />
  );
}
